import { inject, Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import {
  ANALYSIS_PROVIDER_PORT,
} from '../../../analysis/application/ports/analysis-provider.port';
import { AnalysisListFilters } from '../../../analysis/application/entities/analysis-list-filters.entity';
import { AnalysisListItem } from '../../../analysis/application/entities/analysis-list-item.entity';
import { AnalysisListResult, AnalysisOverviewStats } from '../../../analysis/application/entities/analysis-list-result.entity';
import { PaginatedResult } from '../../../common/entities/paginated-result.entity';
import { CorrectiveActionSummary } from '../../application/entities/corrective-action-summary.entity';
import { RECENT_CORRECTIVE_ACTIONS_PROVIDER_PORT } from '../../application/ports/recent-corrective-actions-provider.port';
import { DashboardData } from '../../application/entities/dashboard-data.entity';
import { DashboardFilters } from '../../application/entities/dashboard-filters.entity';
import { DashboardProviderPort } from '../../application/ports/dashboard-provider.port';
import { DashboardStats } from '../../application/entities/dashboard-stats.entity';
import { AnalysisSummary } from '../../application/entities/analysis-summary.entity';

@Injectable({ providedIn: 'root' })
export class DashboardProviderService implements DashboardProviderPort {
  private readonly analysisPort = inject(ANALYSIS_PROVIDER_PORT);
  private readonly actionsPort = inject(RECENT_CORRECTIVE_ACTIONS_PROVIDER_PORT);

  getDashboard(filters?: DashboardFilters): Observable<DashboardData> {
    return forkJoin({
      analyses: this.analysisPort.list(toAnalysisFilters(filters)),
      // Si las acciones fallan, el dashboard igual carga: la tabla queda vacía en vez de bloquearlo todo.
      actions: this.actionsPort.getRecent(filters).pipe(catchError(() => of(EMPTY_ACTIONS_PAGE))),
    }).pipe(
      map(({ analyses, actions }) => toDashboardData(analyses, actions)),
    );
  }

  exportExcel(filters?: DashboardFilters): Observable<Blob> {
    return this.analysisPort.exportExcel(toAnalysisFilters(filters));
  }
}

// ─── Mapeo de filtros ─────────────────────────────────────────────────────────

function toAnalysisFilters(filters?: DashboardFilters): AnalysisListFilters | undefined {
  if (!filters) return undefined;
  return {
    locationUuid: filters.plant,
    riskLevel: filters.riskLevel,
    client: filters.client,
    searchQuery: filters.searchQuery,
    page: filters.page,
    limit: filters.limit,
  };
}

// ─── Mapeo de resultados ──────────────────────────────────────────────────────

const EMPTY_ACTIONS_PAGE: PaginatedResult<CorrectiveActionSummary> = {
  items: [],
  paging: { init: 0, limit: 5, total: 0, totalPages: 0 },
};

function toDashboardData(result: AnalysisListResult, actions: PaginatedResult<CorrectiveActionSummary>): DashboardData {
  return {
    stats: toStats(result),
    recentAnalyses: {
      items: result.items.map(toAnalysisSummary),
      paging: result.paging,
    },
    recentCorrectiveActions: actions,
  };
}

function toStats(result: AnalysisListResult): DashboardStats {
  const s = result.stats;
  return {
    totalAnalysis: s.total,
    analysisInProgress: s.inProgress,
    analysisClosed: s.closed,
    openCorrectiveActions: s.requiresAction,
    overdueActions: s.overdue,
    generalCompliance: toGeneralCompliance(s),
  };
}

function toGeneralCompliance(s: AnalysisOverviewStats): number {
  // Cumplimiento real del backend (PBI 1003): 0-100 con un decimal; la barra muestra enteros.
  // Sin medidas el backend entrega 0; el mapper ya normaliza un campo ausente a 0.
  return Math.round(s.compliance);
}

function toAnalysisSummary(item: AnalysisListItem): AnalysisSummary {
  return {
    id: item.id,
    title: item.title,
    description: item.description,
    currentPl: item.currentPl,
    requiredPl: item.requiredPl,
    riskLevel: item.riskLevel,
    status: item.status,
    tasksCompleted: item.tasksCompleted,
    tasksTotal: item.tasksTotal,
    progressPercent: item.progressPercent,
    locationPath: item.locationPath,
    consultant: item.consultant,
    client: item.client,
    updatedAt: item.updatedAt,
    imageUrl: item.imageUrl,
  };
}
